import * as React from 'react'
import { clsx } from 'clsx'

import {
  BlockChange,
  isSelectBlock,
  type SelectBlock,
} from '@basehub/data/replicache/index'

import Selector from '~/sotano-kit/primitives/selector'
import { TreeNode } from '@basehub/utils/tree/index'

export type SelectBlockValueDiff = {
  [value: SelectBlock['value'][number]]: {
    added?: boolean
    removed?: boolean
    modified?: boolean
  }
}

export const SelectBlockDiff = ({
  diff,
  condition,
  status,
}: {
  diff: SelectBlockValueDiff
  condition: 'added' | 'removed'
  status: BlockChange['status']
}) => {
  const renderResult = React.useCallback(() => {
    const diffEntries = Object.entries(diff)

    if (!diffEntries.length) {
      return <p className="text-dark-muted absolute">No values yet</p>
    }

    const filteredDiffEntries = diffEntries.filter(([_, valueCondition]) => {
      if (valueCondition.removed && condition === 'added') return false
      if (valueCondition.added && condition === 'removed') return false
      return true
    })

    const asd = filteredDiffEntries.map(([value, valueCondition], index) => {
      const isLastItem = index >= filteredDiffEntries.length - 1

      return (
        <p
          key={index}
          className={clsx(
            !isLastItem ? 'pr-7 before:right-6' : 'before:right-0',
            'relative flex items-center before:absolute z-10 before:rounded before:-z-10 before:content-[""] before:-inset-0.5',
            {
              'before:bg-green-diff/30': valueCondition.added,
              'before:bg-red-diff/50': valueCondition.removed,
              'before:bg-yellow/60': valueCondition.modified,
            }
          )}
        >
          <span className="leading-none">{value}</span>
          {!isLastItem && (
            <span className="absolute right-1 text-dark-faint h-[14px] w-[14px] text-lg flex items-center justify-center">
              -
            </span>
          )}
        </p>
      )
    })

    return asd
  }, [diff, condition])

  const id = React.useId()

  return (
    <div
      className={clsx(
        condition === 'added' && status === 'deleted' && 'opacity-30'
      )}
    >
      <Selector
        id={`select-diff-${id}`}
        search={''}
        setSearch={() => {}}
        onSelect={() => {}}
        options={() => []}
        disabled
        renderResult={renderResult}
      />
    </div>
  )
}

export const getSelectDiff = ({
  change,
  condition,
}: {
  change: TreeNode<BlockChange>['value']
  condition: 'removed' | 'added'
}) => {
  let diff: SelectBlockValueDiff = {}
  const isDeletedAndConditionIsAdded =
    change.status === 'deleted' && condition === 'added'
  const prev = change.conflicting ?? change.previous

  if (!prev) {
    diff = (change.current as SelectBlock).value.reduce((acum, value) => {
      acum[value] = { added: true }
      return acum
    }, diff)
  } else if (!change.current) {
    if (isSelectBlock(prev)) {
      diff = prev.value.reduce((acum, value) => {
        acum[value] = { removed: !isDeletedAndConditionIsAdded }
        return acum
      }, diff)
    }
  } else {
    if (isSelectBlock(prev)) {
      diff = prev.value.reduce((acum, targetBlockId) => {
        acum[targetBlockId] = {
          removed: !(change.current as SelectBlock).value.includes(
            targetBlockId
          ),
        }
        return acum
      }, diff)
    }

    diff = (change.current as SelectBlock).value.reduce(
      (acum, targetBlockId) => {
        if (acum[targetBlockId]) return acum
        acum[targetBlockId] = {
          added: isSelectBlock(prev)
            ? !prev.value.includes(targetBlockId)
            : true,
        }

        return acum
      },
      diff
    )
  }

  return diff
}
